import React from "react";
import { Modal, Form, Input, Row, Col, Typography, Divider, Button, Select, Steps } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  SyncOutlined,
  ShoppingOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import "./StoreEditPopup.css";

const { Text } = Typography;
const { Step } = Steps;

const stepIcons = {
  wait: <ClockCircleOutlined />,
  process: <LoadingOutlined />,
  finish: <CheckCircleFilled style={{ color: "#52c41a" }} />,
  error: <CloseCircleFilled style={{ color: "#ff4d4f" }} />,
};

const stepMeta = [
  { icon: <DatabaseOutlined />, title: "Creating Store & Saving Pincodes" },
  { icon: <SyncOutlined />, title: "Syncing to Rapsap Operations" },
  { icon: <ShoppingOutlined />, title: "Importing GoFrugal Stock" },
];

export default function StoreEditPopup({
  visible = false,
  loading = false,
  form,
  isCreateMode = false,
  gofrugalStoreOptions = [],
  loadingGofrugalStores = false,
  onCancel,
  onSave,
  onboardingActive = false,
  onboardingSteps = [],
  onRetry,
}) {
  const [, setFormVersion] = React.useState(0);

  const allDone = onboardingSteps.length > 0 && onboardingSteps.every(s => s.status === "finish" || s.status === "error");
  const hasErrors = onboardingSteps.some(s => s.status === "error");
  const allSuccess = onboardingSteps.length > 0 && onboardingSteps.every(s => s.status === "finish");

  // Form validation (only needed when not in onboarding mode)
  const values = !onboardingActive ? form.getFieldsValue(true) : {};
  const pincodes = Array.isArray(values.pincodes) ? values.pincodes : [];
  const hasPincodeErrors =
    !onboardingActive &&
    (pincodes.length < 1 ||
      pincodes.some(
        (item) =>
          !item ||
          !item.pincode ||
          !/^\d{6}$/.test(String(item.pincode)) ||
          !item.description ||
          !String(item.description).trim()
      ));

  const requiredFields = isCreateMode
    ? ["name", "code", "contact_no", "gofrugal_outlet_id"]
    : ["name", "code", "contact_no"];

  const hasMissingRequiredValues = !onboardingActive && requiredFields.some((fieldName) => {
    const fieldValue = values[fieldName];
    return (
      fieldValue === undefined ||
      fieldValue === null ||
      String(fieldValue).trim() === ""
    );
  });

  const hasContactError =
    !onboardingActive &&
    values.contact_no !== undefined &&
    values.contact_no !== null &&
    String(values.contact_no).trim() !== "" &&
    !/^\d{10}$/.test(String(values.contact_no));

  const hasGofrugalError =
    !onboardingActive &&
    isCreateMode &&
    values.gofrugal_outlet_id !== undefined &&
    values.gofrugal_outlet_id !== null &&
    String(values.gofrugal_outlet_id).trim() !== "" &&
    (Number.isNaN(Number(values.gofrugal_outlet_id)) ||
      Number(values.gofrugal_outlet_id) <= 0);

  const hasLatError =
    !onboardingActive &&
    values.lat !== undefined &&
    values.lat !== null &&
    String(values.lat).trim() !== "" &&
    (Number.isNaN(Number(values.lat)) ||
      Number(values.lat) < -90 ||
      Number(values.lat) > 90);

  const hasLngError =
    !onboardingActive &&
    values.lng !== undefined &&
    values.lng !== null &&
    String(values.lng).trim() !== "" &&
    (Number.isNaN(Number(values.lng)) ||
      Number(values.lng) < -180 ||
      Number(values.lng) > 180);

  const hasRadiusError =
    !onboardingActive &&
    values.radius !== undefined &&
    values.radius !== null &&
    String(values.radius).trim() !== "" &&
    (Number.isNaN(Number(values.radius)) || Number(values.radius) < 0);

  const hasValidationErrors = !onboardingActive && form
    .getFieldsError()
    .some((field) => field.errors.length > 0);

  const isSubmitDisabled =
    loading ||
    hasMissingRequiredValues ||
    hasPincodeErrors ||
    hasContactError ||
    hasGofrugalError ||
    hasLatError ||
    hasLngError ||
    hasRadiusError ||
    hasValidationErrors;

  const renderFooter = () => {
    if (onboardingActive) {
      const buttons = [];
      if (hasErrors && allDone) {
        buttons.push(
          <Button key="retry" type="primary" icon={<ReloadOutlined />} onClick={onRetry} size="large">
            Retry
          </Button>
        );
      }
      if (allDone) {
        buttons.push(
          <Button key="close" onClick={onCancel} size="large" type={allSuccess ? "primary" : "default"}>
            {allSuccess ? "Done" : "Close"}
          </Button>
        );
      }
      return buttons;
    }

    return [
      <Button key="cancel" onClick={onCancel} disabled={loading} size="large">
        Cancel
      </Button>,
      <Button
        key="save"
        type="primary"
        onClick={onSave}
        disabled={isSubmitDisabled}
        loading={loading}
        size="large"
      >
        {isCreateMode ? "Create Store" : "Save Changes"}
      </Button>,
    ];
  };

  const getModalTitle = () => {
    if (onboardingActive) {
      if (allSuccess) return "Store Setup Complete";
      if (hasErrors && allDone) return "Store Setup - Issues Found";
      return "Setting Up Store...";
    }
    return isCreateMode ? "Create Store" : "Edit Store";
  };

  return (
    <Modal
      className="settings-store-edit-modal"
      title={getModalTitle()}
      visible={visible}
      centered
      maskClosable={!loading && !onboardingActive}
      closable={!loading && (!onboardingActive || allDone)}
      width={760}
      footer={renderFooter()}
      onCancel={onCancel}
      onOk={onSave}
    >
      {onboardingActive ? (
        <div className="settings-onboarding-container">
          <div className="settings-onboarding-steps">
            <Steps direction="vertical" size="default">
              {onboardingSteps.map((step, index) => (
                <Step
                  key={index}
                  title={
                    <span className={`settings-onboarding-step-title ${step.status}`}>
                      {step.title}
                    </span>
                  }
                  description={
                    <span className={`settings-onboarding-step-desc ${step.status}`}>
                      {step.description}
                    </span>
                  }
                  status={step.status}
                  icon={stepIcons[step.status] || stepMeta[index]?.icon}
                />
              ))}
            </Steps>
          </div>

          {allSuccess && (
            <div className="settings-onboarding-success-banner">
              <CheckCircleFilled style={{ color: "#52c41a", fontSize: 20 }} />
              <Text strong>Store is ready to use!</Text>
            </div>
          )}

          {hasErrors && allDone && (
            <div className="settings-onboarding-error-banner">
              <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 20 }} />
              <Text>Some steps had issues. You can retry or close and fix later.</Text>
            </div>
          )}
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onValuesChange={() => setFormVersion((version) => version + 1)}
        >
          <div className="settings-edit-block">
            <Text className="settings-edit-block-title">Store Information</Text>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Store Name"
                  name="name"
                  rules={[{ required: true, message: "Store name is required" }]}
                >
                  <Input placeholder="Enter store name" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Store Code"
                  name="code"
                  rules={[{ required: true, message: "Store code is required" }]}
                >
                  <Input placeholder="Enter store code" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label="Description" name="description">
                  <Input.TextArea rows={4} placeholder="Enter store description" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="Contact Number"
                  name="contact_no"
                  rules={[
                    { required: true, message: "Contact number is required" },
                    {
                      pattern: /^\d{10}$/,
                      message: "Enter a valid 10-digit contact number",
                    },
                  ]}
                >
                  <Input maxLength={10} placeholder="Enter 10-digit number" size="large" />
                </Form.Item>
              </Col>
              {isCreateMode && (
                <Col xs={24}>
                  <Form.Item
                    label="Gofrugal Store"
                    name="gofrugal_outlet_id"
                    rules={[
                      { required: true, message: "Please select a GoFrugal store" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === "") {
                            return Promise.resolve();
                          }
                          const outletId = Number(value);
                          if (Number.isNaN(outletId) || outletId <= 0) {
                            return Promise.reject(
                              new Error("Gofrugal outlet id must be a positive number")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Select
                      size="large"
                      showSearch
                      allowClear
                      loading={loadingGofrugalStores}
                      placeholder="Select GoFrugal store"
                      options={gofrugalStoreOptions}
                      filterOption={(input, option) =>
                        String(option?.label || "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </div>

          <Divider className="settings-edit-divider" />

          <div className="settings-edit-block">
            <Text className="settings-edit-block-title">Location Details</Text>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label="Latitude"
                  name="lat"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === "") {
                          return Promise.resolve();
                        }
                        const lat = Number(value);
                        if (Number.isNaN(lat) || lat < -90 || lat > 90) {
                          return Promise.reject(
                            new Error("Latitude must be between -90 and 90")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="-90 to 90" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label="Longitude"
                  name="lng"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === "") {
                          return Promise.resolve();
                        }
                        const lng = Number(value);
                        if (Number.isNaN(lng) || lng < -180 || lng > 180) {
                          return Promise.reject(
                            new Error("Longitude must be between -180 and 180")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="-180 to 180" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label="Radius"
                  name="radius"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === "") {
                          return Promise.resolve();
                        }
                        const radius = Number(value);
                        if (Number.isNaN(radius) || radius < 0) {
                          return Promise.reject(new Error("Radius must be a positive number"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="Enter radius" size="large" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Divider className="settings-edit-divider" />

          <div className="settings-edit-block">
            <Text className="settings-edit-block-title">Serviceable Pin Code</Text>
            <Form.List name="pincodes">
              {(fields, { add, remove }) => (
                <>
                  <div className="settings-edit-pincode-table">
                    <div className="settings-edit-pincode-head">
                      <div>Pin Code</div>
                      <div>Description</div>
                      <div>Action</div>
                    </div>

                    {fields.length ? (
                      fields.map(({ key, name, ...restField }) => (
                        <div className="settings-edit-pincode-row" key={key}>
                          <Form.Item
                            {...restField}
                            name={[name, "pincode"]}
                            rules={[
                              { required: true, message: "Pin code is required" },
                              {
                                pattern: /^\d{6}$/,
                                message: "Enter a valid 6-digit pin code",
                              },
                            ]}
                          >
                            <Input maxLength={6} placeholder="Ex. 400615" size="large" />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, "description"]}
                            rules={[{ required: true, message: "Description is required" }]}
                          >
                            <Input placeholder="Enter area name" size="large" />
                          </Form.Item>

                          <div className="settings-edit-pincode-action">
                            <Button
                              danger
                              onClick={() => remove(name)}
                              disabled={loading || fields.length === 1}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <Text type="secondary">No pincodes added yet.</Text>
                    )}
                  </div>

                  <Button
                    className="settings-edit-pincode-add"
                    onClick={() => add({ pincode: "", description: "" })}
                    disabled={loading}
                  >
                    Add Pin Code
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          {isCreateMode && (
            <>
              <Divider className="settings-edit-divider" />
              <div className="settings-onboarding-preview">
                <Text className="settings-edit-block-title">What happens when you create</Text>
                <div className="settings-onboarding-preview-steps">
                  {stepMeta.map((s, i) => (
                    <div key={i} className="settings-onboarding-preview-step">
                      <span className="settings-onboarding-preview-icon">{s.icon}</span>
                      <Text>{s.title}</Text>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Form>
      )}
    </Modal>
  );
}
